/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import BarcodeScanner from "@eisinc/App/Components/BarcodeScanner/BarcodeScanner";
import TextField, { TextFieldPresentationProps } from "@eisinc/MobiusOverrides/TextField";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@eisinc/Overrides/Widgets/Header/AutocompleteProducts";
import AutocompleteSuggestions from "@eisinc/Overrides/Widgets/Header/AutocompleteSuggestions";
import SearchHistory, { SearchHistoryStyles } from "@eisinc/Overrides/Widgets/Header/SearchHistory";
import { screen } from "@eisinc/XDL/Helpers";
import color from "@eisinc/XDL/Utilities/color";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Search from "@insite/mobius/Icons/Search";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

export interface XngAutocompleteModel extends AutocompleteModel {
    querySuggestions: any;
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);

    const settingsCollection = getSettingsCollection(state);
    let searchKeystrokeDelay = null;
    let isConfiguredForCoveo = null;
    let enableQuerySuggest = null;
    let querySuggestionKeystrokeDelay = null;

    if (settingsCollection) {
        searchKeystrokeDelay = (settingsCollection?.customSettings as any)?.["searchKeystrokeDelay"];
        isConfiguredForCoveo = (settingsCollection?.coveoSettings as any)?.["isConfiguredForCoveo"] === true;
        enableQuerySuggest = (settingsCollection?.coveoSuggestSettings as any)?.["enableQuerySuggest"] === true;
        querySuggestionKeystrokeDelay = (settingsCollection?.coveoSuggestSettings as any)?.[
            "querySuggestionKeystrokeDelay"
        ];
    }

    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        searchKeystrokeDelay,
        isConfiguredForCoveo,
        enableQuerySuggest,
        querySuggestionKeystrokeDelay,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: XngAutocompleteModel;
    isLoading: boolean;
    enterKey: boolean;
    highlightedIndex: number;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteProductColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
}

const autocompleteHeaderText: TypographyPresentationProps = {
    size: "16px",
    lineHeight: "18px",
    css: css`
        text-transform: uppercase;
        letter-spacing: 0.3px;
        text-align: left;
        margin: 30px -40px 5px -40px;
        padding: 10px 40px;
        background-color: ${color("common.ui5", 0.35)};

        &:first-child {
            margin-top: 0;
        }
    `,
};

const columnWidth = 375;

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: {
            src: Search,
            css: css`
                background-color: ${color("primary.main")};
                color: ${color("primary.contrast")};
                border-radius: 0 !important;
                top: 0 !important;
                right: 0 !important;
                width: 40px !important;
                height: 40px !important;

                svg {
                    height: 25px;
                    width: 25px;
                }
            `,
        },
        border: "rectangle",
        cssOverrides: {
            formInputWrapper: css``,
            inputSelect: css`
                border-width: 2px;
                padding-right: 82px;
                text-overflow: ellipsis;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
        xPosition: "middle",
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: center;
            max-width: 600px;
            position: relative;

            ${screen("lte", "sm")`
                max-width: none;
            `}

            .arrow-up-icon {
                display: block;
                position: absolute;
                top: -5px;
                left: 55px;
                border: 10px solid transparent;
                border-bottom: 10px solid ${color("text.main")};
            }
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
        css: css`
            background-color: transparent;
            box-shadow: none;

            @media (max-width: 768px) {
                width: 100%;
            }

            @media (max-width: 481px) {
                padding: 0;
                box-shadow: 0 15px 10px rgb(0, 0, 0, 0.1);
            }
        `,
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            background-color: ${color("primary.contrast")};
            box-shadow: 0 2px 8px 0 ${color("secondary.main", 0.1)};
            position: relative;

            .add-right-border {
                border-right: 1px solid ${getColor("common.ui6")};
            }

            ${screen("lte", "md")`
                flex-direction: column;
                max-height: 70vh;
                overflow-y: auto;

                .add-right-border {
                    border-right: none;
                }
            `}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: ${columnWidth}px;
            padding: 0 40px;
            margin: 30px 0;
            max-height: 70vh;
            overflow-y: auto;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: ${columnWidth * 2}px;
                            max-height: none;
                            overflow-y: visible;
                        `,
                    ],
                    "max",
                )}

            ${screen("lte", "sm")`
                width: 100%;
            `}
        `,
    },
    autocompleteProductColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            width: ${columnWidth * 2}px;
            margin: 15px 0 0 0;
            max-height: 70vh;
            overflow-y: auto;
            box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.1);

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            width: ${columnWidth}px;
                        `,
                    ],
                    "max",
                )}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: ${columnWidth * 2}px;
                            max-height: none;
                            overflow-y: visible;
                        `,
                    ],
                    "max",
                )}

            ${screen("lte", "sm")`
                width: 100%;
            `}
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: autocompleteHeaderText,
        categoryItemText: {
            color: "common.ui6",
        },
        focusedCategoryItemText: {
            color: "text.main",
        },
        link: {
            color: "text.main",
        },
    },
    autocompleteBrandsStyles: {
        headerText: autocompleteHeaderText,
        brandItemText: {
            color: "common.ui6",
        },
        link: {
            color: "text.main",
        },
    },
    autocompleteContentStyles: {
        headerText: autocompleteHeaderText,
        link: {
            color: "text.main",
        },
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            enterKey: false,
            highlightedIndex: -1,
        };
    }

    arrowUpIcon = React.createRef<HTMLSpanElement>();
    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;
    isQuerySuggestionEnabled = this.props.isConfiguredForCoveo && this.props.enableQuerySuggest;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products, querySuggestions } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(querySuggestions?.suggestions || [])
            : (querySuggestions?.suggestions || [])
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        if (this.state.enterKey && !this.state.autoCompleteModel) {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.properties["isSearchTermRedirect"] === "true") {
            this.goToUrl(`/${this.props.searchPath}?query=${this.state.query}`, false);
        } else if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            localStorage.setItem("isSearchListActive", JSON.stringify(true));
            this.goToUrl(`/${this.props.searchPath}?query=${this.state.query}`, false);

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    doLoadAutocomplete = debounce(
        (query: string) => {
            let querySuggestionParams = {};

            if (this.isQuerySuggestionEnabled) {
                querySuggestionParams = {
                    searchHub: "listing",
                    tab: "",
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    locationHref: document?.location?.href,
                    referringUrl: document?.referrer,
                };

                this.setState({
                    highlightedIndex: -1,
                });
            }

            this.props.getAutocompleteModel({
                query,
                isQuerySuggestionEnabled: this.isQuerySuggestionEnabled,
                ...querySuggestionParams,
                onSuccess: (result: any) => {
                    if (query !== this.state.query) {
                        return;
                    }
                    this.setState({ isLoading: false });
                    if (this.showAutocomplete) {
                        this.setState({
                            autoCompleteModel: result,
                        });
                        this.openAutocomplete();
                    }

                    if (!result) {
                        document.querySelector("body")?.classList?.remove("shade");
                    }

                    if (this.state.enterKey && result) {
                        this.setState({ enterKey: false });
                        this.doSearch();
                    }
                },
                onComplete(resultProps: any) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.(resultProps.apiResult);
                    }
                },
            } as any);
        },
        this.isQuerySuggestionEnabled
            ? this.props.querySuggestionKeystrokeDelay
                ? Number(this.props.querySuggestionKeystrokeDelay)
                : 500
            : this.props.searchKeystrokeDelay
            ? Number(this.props.searchKeystrokeDelay)
            : 200,
    );

    loadAutocomplete = (query: string) => {
        this.setState({ isLoading: true });
        this.doLoadAutocomplete(query);
    };

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };

    handleInputFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.handleInputChange(event);
    };

    handleKeyDown = (event: any) => {
        if (event.key === "ArrowUp") {
            this.setState({
                highlightedIndex: Math.max(this.state.highlightedIndex - 1, -1),
            });
        } else if (event.key === "ArrowDown") {
            this.setState({
                highlightedIndex: Math.min(
                    this.state.highlightedIndex + 1,
                    (this.state.autoCompleteModel?.querySuggestions?.suggestions?.length || 0) - 1,
                ),
            });
        } else if (event.key === "Enter") {
            event.preventDefault();
            this.handleEnterKey();
        }
    };

    handleEnterKey = () => {
        if (this.state.highlightedIndex >= 0) {
            const selectedSuggestion =
                this.state.autoCompleteModel?.querySuggestions?.suggestions[this.state.highlightedIndex].suggestion;
            const url = `/Search?query=${selectedSuggestion}`;

            this.setState({
                query: selectedSuggestion,
            });
            this.props.history.push(url);
            this.closeAutocomplete();
        }
    };

    openAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).openPopover();

        if (this.isPopoverVisible()) {
            document.querySelector("body")?.classList?.add("shade");
        }
    };

    closeAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
        document.querySelector("body")?.classList?.remove("shade");
    };

    isPopoverVisible() {
        let isVisible: boolean | null = false;
        let isItVisible: boolean | null = false;

        if ((this.state.query || this.isQuerySuggestionEnabled) && this.state.autoCompleteModel) {
            const { categories, brands, content, products, querySuggestions } = this.state.autoCompleteModel;
            isVisible =
                (categories && categories.length > 0) ||
                (brands && brands.length > 0) ||
                (content && content.length > 0) ||
                (products && products.length > 0) ||
                (querySuggestions && querySuggestions?.suggestions.length > 0);
        } else if ((this.state.query || this.isQuerySuggestionEnabled) && this.state.isLoading) {
            isItVisible = true;
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = searchHistoryItems && searchHistoryItems.length > 0;
        }

        isItVisible = !isItVisible ? isVisible || this.state.isLoading : isItVisible;

        return isItVisible;
    }

    getPopoverWidth() {
        let width = 0;
        if (window.innerWidth <= this.props.theme.breakpoints.values[2]) {
            width += columnWidth * 2;
        } else if (window.innerWidth <= this.props.theme.breakpoints.values[3]) {
            width += columnWidth * 2;
        } else if (window.innerWidth <= this.props.theme.breakpoints.values[4]) {
            if ((this.state.query || this.isQuerySuggestionEnabled) && this.state.autoCompleteModel) {
                const { categories, brands, content, products, querySuggestions } = this.state.autoCompleteModel;
                if (
                    (categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (querySuggestions && querySuggestions?.suggestions?.length > 0)
                ) {
                    width += columnWidth;
                }

                if (products && products.length > 0) {
                    width += columnWidth;
                }
            }

            if (!this.state.query) {
                width += columnWidth;
            }
        } else {
            if ((this.state.query || this.isQuerySuggestionEnabled) && this.state.autoCompleteModel) {
                const { categories, brands, content, products, querySuggestions } = this.state.autoCompleteModel;
                if (
                    (categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (querySuggestions && querySuggestions?.suggestions?.length > 0)
                ) {
                    width += columnWidth;
                }

                if (products && products.length > 0) {
                    width += columnWidth * 2;
                }
            }

            if (!this.state.query) {
                width += columnWidth;
            }
        }

        return width + 50;
    }

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            const popoverWidth = this.getPopoverWidth();
            positionStyle.top = rect.bottom;

            positionStyle.left = rect.left - 50;
            if (positionStyle.left + popoverWidth > window.innerWidth) {
                positionStyle.left -= positionStyle.left + popoverWidth - window.innerWidth;
            }
            positionStyle.left = positionStyle.left < 0 ? 0 : positionStyle.left;

            if (this.arrowUpIcon && this.arrowUpIcon.current) {
                this.arrowUpIcon.current.style.left = `${rect.left - positionStyle.left + 15}px`;
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.isQuerySuggestionEnabled && this.state.highlightedIndex >= 0) {
                    break;
                }
                this.setState({ enterKey: true });
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
            autoCompleteModel: undefined,
        });
        document.querySelector("body")?.classList?.remove("shade");
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products, querySuggestions } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) +
            (categories?.length || 0) +
            (content?.length || 0) +
            (brands?.length || 0) +
            (querySuggestions?.suggestions?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true, suggestion: string | undefined = undefined) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        if (suggestion) {
            this.setState({
                // don't use stale results on next search and go to wrong PDP
                autoCompleteModel: undefined,
                query: suggestion,
            });
        } else {
            this.setState({
                // don't use stale results on next search and go to wrong PDP
                autoCompleteModel: undefined,
            });
        }

        this.props.history.push(url);
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }
        // eslint-disable-next-line no-constant-condition
        if (!this.state.autoCompleteModel && this.state.isLoading) {
            return (
                <StyledWrapper
                    css={css`
                        position: relative;
                        height: 100px;
                        width: ${columnWidth}px;
                        background-color: ${color("primary.contrast")};
                        box-shadow: 0 2px 8px 0 ${color("secondary.main", 0.1)};
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        ${screen("lte", "md")`
                            width: ${columnWidth * 2}px;
                            max-height: none;
                            overflow-y: visible;`}

                        ${screen("lte", "sm")`
                            width: 100%; `}
                    `}
                >
                    <LoadingSpinner color="primary.main" />
                </StyledWrapper>
            );
        }
        if (!this.state.autoCompleteModel) {
            return null;
        }

        const { categories, brands, content, products, querySuggestions } = this.state.autoCompleteModel || {};
        const suggestions = querySuggestions?.suggestions;

        return (
            <StyledWrapper {...this.styles.autocompleteWrapper}>
                {((!this.isQuerySuggestionEnabled &&
                    ((categories && categories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (content && content.length > 0))) ||
                    (this.isQuerySuggestionEnabled && suggestions && suggestions.length > 0)) && (
                    <StyledWrapper
                        {...this.styles.autocompleteColumnWrapper}
                        className={products && products.length > 0 ? "add-right-border" : undefined}
                    >
                        {this.isQuerySuggestionEnabled ? (
                            <AutocompleteSuggestions
                                highlightedIndex={this.state.highlightedIndex}
                                suggestions={suggestions}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                extendedStyles={this.styles.autocompleteContentStyles}
                            />
                        ) : (
                            <>
                                <AutocompleteCategories
                                    categories={categories}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteCategoriesStyles}
                                />
                                <AutocompleteBrands
                                    brands={brands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                />
                                <AutocompleteContent
                                    content={content}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteContentStyles}
                                />
                            </>
                        )}
                    </StyledWrapper>
                )}
                {products && products.length > 0 && (
                    <StyledWrapper {...this.styles.autocompleteProductColumnWrapper}>
                        <AutocompleteProducts
                            products={products}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteProductsStyles}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;

        const searchInput = (
            <StyledWrapper
                css={css`
                    width: 100%;
                `}
            >
                <Hidden above="sm">
                    <BarcodeScanner id={searchId}></BarcodeScanner>
                </Hidden>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{ onClick: this.doSearch }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    onFocus={this.handleInputFocus}
                    onKeyDown={this.handleKeyDown}
                    placeholder={translate("Search by product number, name, brand or keyword")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={searchInput}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={this.handlePopoverClose}
                >
                    {this.isPopoverVisible() && (
                        <>
                            {!this.state.query && this.showAutocomplete && !this.isQuerySuggestionEnabled && (
                                <StyledWrapper {...this.styles.autocompleteWrapper}>
                                    <StyledWrapper {...this.styles.autocompleteColumnWrapper}>
                                        <SearchHistory
                                            focusedQuery={this.state.focusedSearchHistoryQuery}
                                            goToUrl={this.goToUrl}
                                            extendedStyles={styles.searchHistoryStyles}
                                            popOverRef={this.popover}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                            {(this.state.query || this.isQuerySuggestionEnabled) && this.renderAutocomplete()}
                            <span className="arrow-up-icon" ref={this.arrowUpIcon}></span>
                        </>
                    )}
                </Popover>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
